import { useCallback, useLayoutEffect, useMemo, useRef, useState, type DragEvent as RDragEvent, type PointerEvent as RPointerEvent, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { choosingTile, type Choosing } from './choosing.ts';
import { arrowHead, boardLayout, boardLines, outPoint, TITLE_H, type BoardLayout } from './layout.ts';
import { frameSpan, type Span } from './frame.ts';
import { leadsOn as leadsOnFrom, lineLook } from './look.ts';
import { linesOf, type TileLine, type TilePhase, type TileStep } from './model.ts';
import { Tile } from './Tile.tsx';

/**
 * A tree drawn whole (design studies 09, 10 and 12): full size and never zoomed, as wide as the page it is in (or as
 * what it draws needs, scrolling across in its frame: a row is never carried on under itself, P-19, and its steps are
 * spread as far apart as their lines need, #78; on a window wider than the page's working width, out to the window's
 * edges, `useFrame`), the page scrolling down it. Each phase a white block between its start
 * line and its end line (P-17), what happens in it under its start line, its steps in rows; its number, name and facts just above its start line, in the band
 * between it and the phase before; every line faint (a step opened draws its own dark, in FocusChain). A click on a
 * step opens it (the tile's own); the board's pointer handling is for drags alone, and the click that ends a drag opens
 * nothing.
 *
 * A phase's closing gate docks in the band below it, centred between its end line and the next phase's start line, the
 * start in the band above the first (design study 21, drawn B; P-17: "very clearly showing that the phase transiton
 * nodes are the only way to get between phases"; the layout's), and the lines to the steps that start the next phase
 * leave its foot. No line is drawn across a phase's lines but to or from its gate: a line to a later phase runs through
 * the transitions (layout `boardLines`); the one that crosses is a late step's line back to an earlier phase, drawn
 * straight across in bengara (design study 21: "A late step’s lines are the only ones that cross a transition"). A line
 * is dashed while it runs to a step only offered or from one (a need offered above the step that needs it), as the way
 * on from a dot is (look.ts `lineLook`).
 *
 * What a drawing may do, it is given: the master and NSB's job draw a line from a step's foot down onto what follows
 * it (`draw`); the master drags a step into another phase (`onMoveToPhase`); a kind dragged from the tools at the top
 * is dropped into a phase (`onAddKind`), or chosen and then added with a phase's own button (`arming`); the master's
 * phase heading opens the phase's card (`onPhase`). While a step is being joined to another (`choose`, design study 31,
 * slice 5), the step is ringed, each it cannot join faint with why, and a press on a tile is the choice (`onPress`):
 * nothing is drawn, dragged or carried at a tile's foot meanwhile.
 */

/** The kinds the tools at the top hand over by dragging. */
export const KIND_DRAG = 'application/x-nsb-kind';

type Wire = { from: string; option: string | null; x: number; y: number; over: string | null; why: string | null };
type Lift = { key: string; dx: number; dy: number; x: number; y: number; moved: boolean };

/**
 * The board's frame and its width, known before the first paint and kept as the window changes: out past the page's
 * working width to the window's, less a gutter, where its column reaches the page's edge (frame.ts, #78).
 */
function useFrame(): [RefObject<HTMLDivElement | null>, number | null, CSSProperties | undefined] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [at, setAt] = useState<{ width: number; style: CSSProperties | undefined } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    const column = el?.parentElement;
    if (!el || !column) return;
    const measure = () => {
      const c = column.getBoundingClientRect();
      const shellEl = column.closest('.shell');
      let shell: Span | null = null;
      if (shellEl) {
        const r = shellEl.getBoundingClientRect();
        const css = getComputedStyle(shellEl);
        shell = { left: r.left + parseFloat(css.paddingLeft), right: r.right - parseFloat(css.paddingRight) };
      }
      const span = frameSpan({ left: c.left, right: c.right }, shell, document.documentElement.clientWidth);
      const wider = span.width > c.width + 1;
      setAt({ width: wider ? span.width : el.clientWidth || c.width, style: wider ? { marginLeft: span.left - c.left, width: span.width } : undefined });
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(column);
    window.addEventListener('resize', measure);
    return () => {
      watch.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  return [ref, at?.width ?? null, at?.style];
}

export function TreeBoard({
  phases,
  steps,
  lines: given,
  label,
  selected = null,
  keepEmpty = false,
  onOpen,
  draw,
  onMoveToPhase,
  onAddKind,
  arming = null,
  actionsFor,
  onPhase,
  choose,
}: {
  phases: readonly TilePhase[];
  steps: readonly TileStep[];
  /** The waits to draw; by default every step's needs. */
  lines?: readonly TileLine[];
  label: string;
  /** A step found or opened: ringed, and brought into view. */
  selected?: string | null;
  /** Every phase drawn, even with nothing in it yet (a drawing steps can be added to). */
  keepEmpty?: boolean;
  onOpen?: (key: string) => void;
  /** Lines drawn by hand: from a step's foot (or a decision's way on) down onto what follows it. */
  draw?: { check: (dependent: string, needed: string) => string | null; onLink: (dependent: string, needed: string, option: string | null) => void };
  onMoveToPhase?: (key: string, phase: string | null) => void;
  onAddKind?: (kind: string, phase: string | null) => void;
  /** A kind chosen in the tools: each phase offers to take it. */
  arming?: { kind: string; word: string } | null;
  /** What a step carries at its foot setting a job up. */
  actionsFor?: (key: string) => ReactNode;
  /** A phase's heading clicked: its card opened (the master's, where a phase's facts are set). */
  onPhase?: (phase: string) => void;
  /** Choosing what a step needs or opens: the step being joined, why one cannot be, and a tile pressed (`pressWhileChoosing`). */
  choose?: Pick<Choosing, 'key' | 'refusal'> & { onPress: (key: string) => void };
}) {
  const [ref, width, frame] = useFrame();
  const lines = useMemo(() => given ?? linesOf(steps), [given, steps]);
  const layout: BoardLayout | null = useMemo(() => (width ? boardLayout(phases, steps, width, { keepEmpty, lines }) : null), [phases, steps, width, keepEmpty, lines]);
  const drawn = useMemo(() => (layout ? boardLines(steps, lines, layout) : []), [layout, steps, lines]);
  const byKey = useMemo(() => new Map(steps.map((s) => [s.key, s])), [steps]);
  /** A dot leads on to a step of the job, through any dots after it: then the line into it is drawn whole. */
  const leadsOn = useCallback((from: string): boolean => leadsOnFrom(steps, from), [steps]);
  const phaseName = useMemo(() => new Map(phases.map((p) => [p.key, p.name])), [phases]);
  const inner = useRef<HTMLDivElement | null>(null);
  const [wire, setWire] = useState<Wire | null>(null);
  const [lift, setLift] = useState<Lift | null>(null);
  const [dropOn, setDropOn] = useState<string | null>(null);
  // The click that ends a line drawn or a step dragged is not a click on a step.
  const quiet = useRef(false);

  // A step found is brought into view.
  useLayoutEffect(() => {
    if (!selected || !layout) return;
    const el = inner.current?.querySelector(`[data-node="${CSS.escape(selected)}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [selected, layout]);

  if (!layout) return <div ref={ref} role="group" aria-label={label} className="min-h-40" style={frame} />;

  const local = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = inner.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  const under = (e: { clientX: number; clientY: number }) => {
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    return {
      key: (el?.closest('[data-node]') as HTMLElement | null)?.dataset.node ?? null,
      phase: (el?.closest('[data-phase]') as HTMLElement | null)?.dataset.phase ?? null,
    };
  };
  const phaseOf = (v: string | null) => (v === null || v === '' ? null : v === '<none>' ? null : v);

  const down = (e: RPointerEvent<HTMLDivElement>) => {
    quiet.current = false;
    if (e.button !== 0) return;
    const t = e.target as HTMLElement;
    const out = t.closest('[data-out]') as HTMLElement | null;
    if (out && draw) {
      e.preventDefault();
      quiet.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      const [x, y] = local(e);
      setWire({ from: out.dataset.out!, option: out.dataset.option ?? null, x, y, over: null, why: null });
      return;
    }
    // A step is lifted only where it can be moved to another phase; held until it moves, so a click stays the tile's.
    if (!onMoveToPhase) return;
    const node = t.closest('[data-node]') as HTMLElement | null;
    if (!node || t.closest('button')) return;
    const key = node.dataset.node!;
    const b = layout.boxes.get(key);
    if (!b) return;
    const [x, y] = local(e);
    setLift({ key, dx: x - b.x, dy: y - b.y, x: b.x, y: b.y, moved: false });
  };
  const move = (e: RPointerEvent<HTMLDivElement>) => {
    if (wire) {
      const [x, y] = local(e);
      const { key } = under(e);
      const over = key && key !== wire.from && !key.startsWith('ghost:') ? key : null;
      setWire({ ...wire, x, y, over, why: over && draw ? draw.check(over, wire.from) : null });
      return;
    }
    if (lift) {
      const [x, y] = local(e);
      const nx = x - lift.dx;
      const ny = y - lift.dy;
      const moved = lift.moved || Math.hypot(nx - layout.boxes.get(lift.key)!.x, ny - layout.boxes.get(lift.key)!.y) > 6;
      if (moved && !lift.moved) {
        quiet.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
      }
      setLift({ ...lift, x: nx, y: ny, moved });
      if (moved) setDropOn(under(e).phase);
    }
  };
  const up = (e: RPointerEvent<HTMLDivElement>) => {
    if (wire) {
      const { key } = under(e);
      if (draw && key && key !== wire.from && !draw.check(key, wire.from)) draw.onLink(key, wire.from, wire.option);
      setWire(null);
      return;
    }
    if (lift) {
      if (lift.moved && onMoveToPhase) {
        const { phase } = under(e);
        const s = byKey.get(lift.key);
        if (phase !== null && s && phaseOf(phase) !== s.phase && !s.end) onMoveToPhase(lift.key, phaseOf(phase));
      }
      setLift(null);
      setDropOn(null);
    }
  };
  const dragOver = (e: RDragEvent<HTMLElement>, phase: string) => {
    if (!onAddKind || !e.dataTransfer.types.includes(KIND_DRAG)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (dropOn !== phase) setDropOn(phase);
  };
  const drop = (e: RDragEvent<HTMLElement>, phase: string) => {
    const kind = e.dataTransfer.getData(KIND_DRAG);
    setDropOn(null);
    if (!kind || !onAddKind) return;
    e.preventDefault();
    onAddKind(kind, phaseOf(phase));
  };

  const wireFrom = wire ? layout.boxes.get(wire.from) : null;
  return (
    <div ref={ref} role="group" aria-label={label} className="overflow-x-auto" style={frame}>
      <div
        ref={inner}
        className="relative"
        style={{ height: layout.height, minWidth: layout.width, touchAction: wire || lift?.moved ? 'none' : undefined }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        // A press inside selected text is never the browser's drag of it, which cancels the pointer and the lift with it
        // (P-34, as on the paper's drawings: `usePaper` `onDragStart`).
        onDragStart={(e) => e.preventDefault()}
        onClickCapture={(e) => {
          if (!quiet.current) return;
          quiet.current = false;
          e.stopPropagation();
          e.preventDefault();
        }}
        onPointerCancel={() => {
          setWire(null);
          setLift(null);
          setDropOn(null);
        }}
      >
        {layout.blocks.map((b, i) => {
          const p = b.phase ? phases.find((x) => x.key === b.phase) : null;
          const key = b.phase ?? '<none>';
          // Its steps: a task done early is its step's, never one of its phase's.
          const count = steps.filter((s) => s.phase === b.phase && !s.end && !s.ghost && !s.early).length;
          // The title stops short of the gate docked beside it in the band.
          const gate = b.docked ? layout.boxes.get(b.docked) : undefined;
          return (
            <div key={key} className="contents">
              <header
                className="absolute left-0 grid grid-cols-[auto_auto_minmax(0,1fr)_auto] items-baseline gap-x-3.5 px-6"
                style={{ top: b.head, height: TITLE_H, right: gate ? undefined : 0, width: gate ? Math.max(0, gate.x - 16) : undefined, alignContent: 'end', paddingBottom: 8 }}
              >
                <span className="font-mono text-[12px] text-ink-3">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="font-display text-[1.875rem] font-normal leading-[1.1]">
                  {p && onPhase ? (
                    <button type="button" title="Open the phase" className="hit text-left decoration-1 underline-offset-4 hover:underline" onClick={() => onPhase(p.key)}>
                      {p.name}
                    </button>
                  ) : (
                    (p?.name ?? 'In no phase yet')
                  )}
                </h3>
                {/* Cut short before the gate docked beside it, the whole on hover. */}
                <span className="truncate font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3" title={(p?.facts ?? []).join(' · ') || undefined}>
                  {(p?.facts ?? [`${count} ${count === 1 ? 'step' : 'steps'}`]).join(' · ')}
                </span>
                {arming && onAddKind ? (
                  <button type="button" className="link whitespace-nowrap text-[0.875rem]" onClick={() => onAddKind(arming.kind, phaseOf(key))}>
                    Add {arming.word} here
                  </button>
                ) : (
                  <span />
                )}
              </header>
              <section
                data-phase={key}
                aria-label={`${p?.name ?? 'In no phase yet'}: ${count} ${count === 1 ? 'step' : 'steps'}`}
                onDragOver={(e) => dragOver(e, key)}
                onDragLeave={() => setDropOn(null)}
                onDrop={(e) => drop(e, key)}
                className={`absolute inset-x-0 border-y border-ink bg-sheet ${dropOn === key ? 'outline outline-2 -outline-offset-2 outline-ink' : ''}`}
                style={{ top: b.y, height: b.h }}
              >
                {p?.what ? <p className="m-0 max-w-[60rem] px-6 pt-2.5 text-[0.875rem] text-ink-2">{p.what}</p> : null}
                {count === 0 && !steps.some((s) => s.phase === b.phase && (s.ghost || s.early)) ? <p className="meta absolute left-6 top-[46px]">Nothing in it yet.</p> : null}
              </section>
            </div>
          );
        })}
        <svg className="pointer-events-none absolute left-0 top-0 z-[1] overflow-visible" width="100%" height={layout.height} aria-hidden="true">
          {drawn.map((l) => {
            const dst = byKey.get(l.dst);
            // Dashed while it runs to or from a step only offered (a line into a dot as the way on from it is); a late
            // step's line back to an earlier phase in bengara, as study 21 drew it.
            const { dashed, late } = lineLook(l, byKey, leadsOn);
            const stroke = late ? 'stroke-alert' : dashed ? 'stroke-rule-strong' : 'stroke-rule-strong opacity-70';
            return (
              <g key={l.id} data-late={late ? '' : undefined}>
                <path d={l.d} fill="none" className={stroke} strokeWidth={late ? 1.5 : 1} strokeDasharray={dashed ? '4 3' : undefined} />
                {dst?.dot ? null : <path d={arrowHead(l.head)} className={late ? 'fill-alert' : 'fill-rule-strong opacity-70'} />}
              </g>
            );
          })}
          {wire && wireFrom
            ? (() => {
                const [x1, y1] = outPoint(wireFrom, byKey.get(wire.from) ?? {}, wire.option);
                return <line x1={x1} y1={y1} x2={wire.x} y2={wire.y} className={wire.why ? 'stroke-alert' : 'stroke-ink'} strokeWidth="1.5" strokeDasharray="4 3" />;
              })()
            : null}
        </svg>
        {steps.map((s) => {
          const b = layout.boxes.get(s.key);
          if (!b) return null;
          const lifted = lift?.moved && lift.key === s.key;
          // Choosing: the step being joined ringed and not pressed; one it cannot join faint, saying why; any other the choice.
          const c = choose ? choosingTile(s.key, choose) : null;
          return (
            <Tile
              key={s.key}
              step={s}
              x={lifted ? lift.x : b.x}
              y={lifted ? lift.y : b.y}
              h={b.h}
              selected={c ? c.ringed : selected === s.key || wire?.over === s.key}
              faint={Boolean(c?.why)}
              why={c?.why ?? null}
              handles={!c && Boolean(draw) && !s.ghost}
              actions={c ? undefined : (actionsFor?.(s.key) ?? undefined)}
              onOpen={c ? (c.ringed ? undefined : () => choose!.onPress(s.key)) : onOpen ? () => onOpen(s.key) : undefined}
            />
          );
        })}
        {wire?.why ? (
          <div className="pointer-events-none absolute z-30 max-w-64 rounded-[2px] bg-ink px-2 py-1 text-[0.8125rem] text-paper" style={{ left: wire.x + 12, top: wire.y + 12 }}>
            {wire.why}
          </div>
        ) : null}
        {lift?.moved && dropOn ? (
          <div className="pointer-events-none absolute z-30 rounded-[2px] bg-ink px-2 py-1 text-[0.8125rem] text-paper" style={{ left: lift.x, top: lift.y - 30 }}>
            Into {dropOn === '<none>' ? 'no phase' : (phaseName.get(dropOn) ?? dropOn)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
